import moment from 'moment-timezone';
import React from 'react';
import { LabeledField } from '../../../components/form-fields/labeled-field';
import { Doc31Configuration } from '../../../types/doc31';
import { doc31FieldId } from '../../../utils/doc31';
import style from './style.module.scss';

/** What every Setup field needs from the form that owns it. */
export interface SetupFormApi {
    values: Doc31Configuration;
    timezone: string;
    /** The edition is closed: every field is disabled. */
    readOnly: boolean;
    /** Sets a value by its formik path (`bonuses.0.entries`). */
    setValue: (formikPath: string, value: unknown) => void;
}

export const LANGUAGES = ['en', 'fr'] as const;
export const LANGUAGE_NAMES = { en: 'English', fr: 'French' } as const;

/** The wall-clock time picked, read in the edition timezone. */
export function fromPickerValue(
    value: moment.Moment | null,
    timezone: string
): string | null {
    return value
        ? moment.tz(value.format('YYYY-MM-DDTHH:mm:ss'), timezone).toISOString()
        : null;
}

const FOCUSABLE =
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), button:not([disabled])';

/**
 * Scrolls to the Setup field a validation path names, or to the closest
 * enclosing wrapper (row, then section) when the field has no own one.
 */
export function scrollToSetupField(path: string): void {
    const segments = path.split('.');
    for (let length = segments.length; length > 0; length -= 1) {
        const element = document.getElementById(
            doc31FieldId(segments.slice(0, length).join('.'))
        );
        if (element) {
            element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
            element
                .querySelector<HTMLElement>(FOCUSABLE)
                ?.focus({ preventScroll: true });
            return;
        }
    }
}

interface SetupFieldProps {
    /** API path: the wrapper id the readiness panel scrolls to. */
    path: string;
    label: React.ReactNode;
    helperText?: string;
    className?: string;
    children: React.ReactNode;
}

export const SetupField = ({
    path,
    label,
    helperText,
    className,
    children,
}: SetupFieldProps) => (
    <div
        id={doc31FieldId(path)}
        className={`${style.field} ${className ?? ''}`}
    >
        <LabeledField label={label} helperText={helperText}>
            {children}
        </LabeledField>
    </div>
);
